import enviro from 'enviro';
import apiClient from 'hub-http/clients/apiClient';
import { getFullUrl } from 'hubspot-url-utils';
import Raven from 'raven-js';
import { createRpcClientV2 } from 'rpc-client-utils';
import { completeRpc } from '../../__generated__/chirp/com/hubspot/shopify/integration/rpc/InstallServiceRpc';
import { APP_SCOPES as scopes, SHOPIFY_SCOPES as shopifyScopes, GOOGLE_WORKSPACE_APP_SCOPES as googleWorkspaceAppScopes, OPTIONAL_WORDPRESS_SCOPES as wordpressScopes, PROXY_DOMAIN_MAP_SCOPES as proxyDomainMapScopes } from 'wordpress-scopes';
import { Metrics } from '../metrics/Metrics';
import { Data } from 'signup-constants/signupData/Data';
import { getHublet } from '../store/app/selectors';
import { getPortalId } from '../store/auth/selectors';
import { updateIntegratedAppAuthResponse } from '../store/integrations/action-creators';
import { getSignupDataValue } from '../store/signup-data/selectors';
import { MetricsCounter } from '../metrics/MetricsCounter';
import { getQueryParam } from '../routing/getQueryParam';
import { Integration } from 'signup-constants/Integration';
const USER_UNAUTHORIZED = 'USER_UNAUTHORIZED';
const rpcClient = createRpcClientV2({
  hubHttpClient: apiClient
});
const INTEGRATED_APP_CONFIG = {
  [Integration.Wordpress]: {
    clientIds: {
      qa: '5441b9e5-b561-451b-b3bc-5340bd8bf41e',
      prod: '6fde4e11-cbcf-4f7c-b1ba-8f57e9b02ae8'
    },
    redirectUrl: '/wordpress/v1/oauth/callback',
    scopes
  },
  [Integration.ShopifyEmbedded]: {
    clientIds: {
      qa: '6017bf91-7afc-4e70-94a1-e953ddec76eb',
      prod: 'b6a21a00-f029-4f69-9348-82ed2f464713'
    },
    redirectUrl: '/shopify-integration/v2/oauth/callback',
    scopes: shopifyScopes
  },
  [Integration.GoogleWorkspaceApp]: {
    clientIds: {
      qa: '72cc7506-bb69-488f-8a80-0102349d626a',
      prod: 'ba7163a9-a4b8-4c9e-b3e0-ee165d05ea50'
    },
    redirectUrl: {
      qa: 'https://script.google.com/macros/d/1eLufcyupnQLhq_TV92QsBJP8DXSAct_040HF3e2BDbbr0F9NuuHo5Ly3/usercallback',
      prod: 'https://script.google.com/macros/d/12mpzSeTVwsh4RVYkQInqU8FvdalMTL4UumOkxiyrR3BzgE24n9yzOJ9G/usercallback'
    },
    scopes: googleWorkspaceAppScopes
  }
};
export const getIntegratedAppRedirectUri = (integration, baseApi) => {
  const config = INTEGRATED_APP_CONFIG[integration];
  if (integration === Integration.GoogleWorkspaceApp) {
    const env = enviro.isQa() ? 'qa' : 'prod';
    return config.redirectUrl[env];
  }
  return `${baseApi}${config.redirectUrl}`;
};
export const authorizeOAuthIntegration = integration => ({
  recaptchaToken,
  hubId,
  hublet,
  dispatch
}) => {
  const baseApi = getFullUrl('app-api', {
    hubletOverride: hublet
  });
  const config = INTEGRATED_APP_CONFIG[integration];
  const requiresContentEmbedScope = getQueryParam('requiresContentEmbedScope') === 'true';
  const clientId = enviro.isQa() ? config.clientIds.qa : config.clientIds.prod;
  const optionalWordpressScopes = requiresContentEmbedScope ? wordpressScopes : proxyDomainMapScopes;
  const optionalScopes = integration === Integration.Wordpress ? optionalWordpressScopes : [];
  const redirectUri = getIntegratedAppRedirectUri(integration, baseApi);
  const data = {
    clientId,
    recaptchaToken,
    hubId,
    redirectUri,
    responseType: 'code',
    scopes: config.scopes,
    optionalScopes
  };
  const oauthApiPath = `/oauth/v2/auth?portalId=${hubId}`;
  const apiClientPromise = apiClient.post(`${baseApi}${oauthApiPath}`, {
    data
  });
  if (integration === Integration.GoogleWorkspaceApp) {
    return apiClientPromise;
  }
  return apiClientPromise.then(response => apiClient.post(`${response.redirectUrl}&portalId=${hubId}`)).then(response => {
    dispatch(updateIntegratedAppAuthResponse(response));
  });
};
export const makeOAuthAuthorizationRequest = integration => (state, dispatch) => {
  const hubId = getPortalId(state);
  if (!hubId) {
    Raven.captureMessage('No portalId found in state.');
    /* eslint-disable-next-line prefer-promise-reject-errors */
    return Promise.reject();
  }
  const recaptchaToken = getSignupDataValue(state, Data.IntegratedAppRecaptchaToken);
  const hublet = getHublet(state);
  return authorizeOAuthIntegration(integration)({
    recaptchaToken,
    hubId,
    hublet,
    dispatch
  });
};
export const connectShopifyStore = async (state, dispatch) => {
  const connectState = getQueryParam('connectState');
  if (!connectState) {
    Raven.captureMessage('No connectState found in query params.');
    /* eslint-disable-next-line prefer-promise-reject-errors */
    return Promise.reject();
  }
  await rpcClient.call(completeRpc, {
    request: {
      connectState
    }
  }).catch(err => {
    Raven.captureException(err, {
      fingerprint: ['connectShopifyStore']
    });
    throw err;
  });
  return makeOAuthAuthorizationRequest(Integration.ShopifyEmbedded)(state, dispatch);
};
export const validateOAuthPermissions = integration => state => {
  const config = INTEGRATED_APP_CONFIG[integration];
  const hubId = getPortalId(state);
  const hublet = getHublet(state);
  const clientId = enviro.isQa() ? config.clientIds.qa : config.clientIds.prod;
  const baseApi = `${getFullUrl('app-api', {
    hubletOverride: hublet
  })}`;
  const redirectUri = getIntegratedAppRedirectUri(integration, baseApi);
  return apiClient.post(`${baseApi}/oauth/v2/application-authorizations?portalId=${hubId}`, {
    data: {
      clientId,
      hubId,
      redirectUri,
      responseType: 'code',
      scopes: config.scopes
    }
  }).catch(err => {
    if (err && err.data) {
      const parsedError = JSON.parse(err.data);
      if (parsedError.status === USER_UNAUTHORIZED) {
        Metrics.counter(MetricsCounter.UserUnauthorized, {
          integration
        }).increment();
      }
    }
    throw err;
  });
};